// GraphOverlay.js
import React, { useState, useEffect, useRef } from 'react';

// Backend will receive this as products_arg
const PRODUCTS_ARG =
  '["Sign","Keychain","Coaster","Lightbox","Desk Organizer","Bookmark","Cake Topper"]';

// Base URL where your analytics JSON files are served.
const JSON_URL_BASE = '/analytics';

// How many bars should be visible in the viewport at once
const MAX_VISIBLE_BARS = 7;
// Fixed width per bar (as % of the bar area width)
const BAR_WIDTH_PERCENT = 13; // 7 * 13% = 91% (+margins)

export default function GraphOverlay({ logs, onClose }) {
  const [selectedKey, setSelectedKey] = useState(null);

  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsStatus, setAnalyticsStatus] = useState('');
  const [barData, setBarData] = useState([]);

  const [userList, setUserList] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [userDataMap, setUserDataMap] = useState(null);

  const [currentTimeframe, setCurrentTimeframe] = useState(null);
  const [timeBuckets, setTimeBuckets] = useState([]);
  const [timeMetrics, setTimeMetrics] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('');

  const [barScales, setBarScales] = useState([]);
  const [lastUpdated, setLastUpdated] = useState('');

  const scrollContainerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const buttonBaseStyle = {
    padding: '4px 10px',
    margin: '0 4px',
    borderRadius: '4px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#ccc',
    backgroundColor: '#f7f7f7',
    cursor: 'pointer',
    fontSize: '12px',
  };

  const makeButtonStyle = (key) =>
    selectedKey === key
      ? {
          ...buttonBaseStyle,
          backgroundColor: '#007bff',
          color: '#fff',
          borderColor: '#007bff',
        }
      : buttonBaseStyle;

  const BAR_COLORS = [
    '#F44336',
    '#2196F3',
    '#4CAF50',
    '#FF9800',
    '#9C27B0',
    '#E91E63',
    '#00BCD4',
    '#795548',
  ];

  const LAST_UPDATE_REGEX = /^last[_ ]?update(d)?$/i;

  const mapKeyToConfig = (key) => {
    switch (key) {
      case 'user_24_hours':
        return { timeframe: '24_hours', folder: 'userAccLytics' };
      case 'user_7_days':
        return { timeframe: '7_days', folder: 'userAccLytics' };
      case 'user_30_days':
        return { timeframe: '30_days', folder: 'userAccLytics' };
      case 'user_1_year':
        return { timeframe: '1_year', folder: 'userAccLytics' };

      case 'time_24_hours':
        return { timeframe: '24_hours_hourly', folder: 'timeAccLytics' };
      case 'time_7_days':
        return { timeframe: '7_days_daily', folder: 'timeAccLytics' };
      case 'time_30_days':
        return { timeframe: '30_days_daily', folder: 'timeAccLytics' };
      case 'time_1_year':
        return { timeframe: '1_year_weekly', folder: 'timeAccLytics' };

      default:
        return { timeframe: '24_hours', folder: 'userAccLytics' };
    }
  };

  const normalizeJsonToBars = (json) => {
    if (!json) return [];

    if (Array.isArray(json)) {
      return json.map((item, idx) => {
        if (item && typeof item === 'object') {
          const label = item.label ?? item.name ?? item.metric ?? `Item ${idx + 1}`;
          const rawVal = item.value ?? item.count ?? item.total ?? 0;
          const value = Number(rawVal) || 0;
          return { label, value };
        }
        return { label: `Item ${idx + 1}`, value: Number(item) || 0 };
      });
    }

    if (typeof json === 'object') {
      return Object.entries(json)
        .filter(([key]) => !LAST_UPDATE_REGEX.test(key))
        .map(([key, val]) => ({
          label: key,
          value: Number(val) || 0,
        }));
    }

    return [];
  };

  const extractTimeBucketsAndMetrics = (timeframe, json) => {
    const buckets = [];
    const metricSet = new Set();

    if (!json) return { buckets: [], metrics: [] };

    if (timeframe === '24_hours_hourly') {
      const hourMap = new Map();

      if (Array.isArray(json)) {
        json.forEach((item, idx) => {
          if (item && typeof item === 'object') {
            const hourCandidate = item.hour ?? item.h ?? item.label ?? item.name ?? idx;
            const hour = Number(hourCandidate);
            if (!Number.isNaN(hour) && hour >= 0 && hour < 24) hourMap.set(hour, item);
          } else {
            const hour = idx;
            if (hour >= 0 && hour < 24) hourMap.set(hour, { Total: Number(item) || 0 });
          }
        });
      } else if (typeof json === 'object') {
        Object.entries(json)
          .filter(([k]) => !LAST_UPDATE_REGEX.test(k))
          .forEach(([k, v]) => {
            const hour = Number(k);
            if (!Number.isNaN(hour) && hour >= 0 && hour < 24) {
              hourMap.set(hour, v && typeof v === 'object' ? v : { Total: Number(v) || 0 });
            }
          });
      }

      const now = new Date();
      const currentHour = now.getHours();
      const orderedHours = [];
      for (let i = 23; i >= 0; i--) {
        const h = (currentHour - i + 24) % 24;
        orderedHours.push(h);
      }

      for (const h of orderedHours) {
        const label = `${String(h).padStart(2, '0')}:00`;
        const rawMetrics = hourMap.get(h);
        const metrics = rawMetrics && typeof rawMetrics === 'object' ? rawMetrics : {};
        buckets.push({ label, metrics });
      }
    } else {
      let entries = [];
      if (Array.isArray(json)) {
        entries = json
          .filter((item) => item && typeof item === 'object')
          .map((item, idx) => ({
            key: item.key ?? item.date ?? item.label ?? `Item ${idx + 1}`,
            metrics: item,
          }));
      } else if (typeof json === 'object') {
        entries = Object.entries(json)
          .filter(([k]) => !LAST_UPDATE_REGEX.test(k))
          .map(([k, v]) => ({
            key: k,
            metrics: v && typeof v === 'object' ? v : { Total: Number(v) || 0 },
          }));
      }

      entries.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
      entries.forEach(({ key, metrics }) => buckets.push({ label: key, metrics }));
    }

    buckets.forEach((b) => {
      const m = b.metrics;
      if (m && typeof m === 'object') Object.keys(m).forEach((name) => metricSet.add(name));
    });

    let metrics = Array.from(metricSet);
    if (metrics.length === 0) {
      metrics = ['Total'];
      buckets.forEach((b) => {
        if (!b.metrics || typeof b.metrics !== 'object') b.metrics = { Total: 0 };
      });
    }

    return { buckets, metrics };
  };

  const computeBarDataFromBuckets = (buckets, metricName) => {
    if (!metricName) return buckets.map((b) => ({ label: b.label, value: 0 }));
    return buckets.map((b) => {
      const metrics = b.metrics || {};
      const raw = metrics[metricName] ?? 0;
      return { label: b.label, value: Number(raw) || 0 };
    });
  };

  // ✅ FIX: keep 90° tilt, but allow multi-line for multi-word labels, and allocate enough height.
  const splitWords = (label) => String(label ?? '').trim().split(/\s+/).filter(Boolean);
  const makeTiltLabelText = (label) => {
    const words = splitWords(label);
    if (words.length <= 1) return String(label ?? '').trim();
    // multi-line (inside rotated element)
    return words.join('\n');
  };
  const labelNeedsMoreRoom = (label) => splitWords(label).length > 1;

  const runAnalytics = async (key) => {
    const { timeframe, folder } = mapKeyToConfig(key);

    console.log('Running analytics:', { key, timeframe, folder });

    setSelectedKey(key);
    setCurrentTimeframe(timeframe);
    setAnalyticsLoading(true);
    setAnalyticsStatus('Running analytics...');
    setBarData([]);
    setBarScales([]);
    setTimeBuckets([]);
    setTimeMetrics([]);
    setLastUpdated('');

    try {
      const res = await fetch('/run-analytics/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          products_arg: PRODUCTS_ARG,
          timeframe,
        }),
      });

      const resText = await res.text().catch(() => '');
      console.log('Analytics backend response:', res.status, resText);

      if (!res.ok) {
        console.error('Analytics error response:', resText);
        setAnalyticsStatus('Error running analytics');
        return;
      }

      const jsonUrl = `${JSON_URL_BASE}/${folder}/${timeframe}.json`;
      const jsonRes = await fetch(`${jsonUrl}?t=${Date.now()}`, { cache: 'no-store' });

      if (!jsonRes.ok) {
        const text = await jsonRes.text().catch(() => '');
        console.error(`Failed to fetch analytics JSON from ${jsonUrl}:`, text);
        setAnalyticsStatus('Analytics JSON not found');
        return;
      }

      const json = await jsonRes.json().catch((e) => {
        console.error('Failed to parse analytics JSON:', e);
        return null;
      });

      if (!json) {
        setAnalyticsStatus('Invalid analytics JSON');
        return;
      }

      let lu = '';
      if (typeof json === 'object' && json !== null) {
        const luKey = Object.keys(json).find((k) => LAST_UPDATE_REGEX.test(k));
        if (luKey) lu = String(json[luKey] ?? '');
      }
      setLastUpdated(lu);

      if (folder === 'userAccLytics') {
        const userObj = json.users && typeof json.users === 'object' ? json.users : json;

        const allUsers = Object.keys(userObj).filter((k) => !LAST_UPDATE_REGEX.test(k));

        setUserDataMap(userObj);
        setUserList(allUsers);
        setTimeBuckets([]);
        setTimeMetrics([]);
        setSelectedMetric('');

        if (allUsers.length === 0) {
          setBarData([]);
          setAnalyticsStatus('No users found in analytics JSON');
          return;
        }

        if (selectedUser && allUsers.includes(selectedUser)) {
          const userStats = userObj[selectedUser];
          if (userStats && typeof userStats === 'object') {
            const bars = normalizeJsonToBars(userStats);
            if (bars.length) {
              const sorted = [...bars].sort((a, b) => b.value - a.value);
              setBarData(sorted.slice(0, 10));
              setAnalyticsStatus(`Showing data for ${selectedUser} (${key})`);
              return;
            }
          }
          setBarData([]);
          setAnalyticsStatus('No data for selected user');
        } else {
          setBarData([]);
          setAnalyticsStatus('Select a user to view data');
        }
        return;
      }

      const { buckets, metrics } = extractTimeBucketsAndMetrics(timeframe, json);

      if (!buckets.length) {
        setAnalyticsStatus('No data in analytics JSON');
        setBarData([]);
        setTimeBuckets([]);
        setTimeMetrics([]);
        return;
      }

      setTimeBuckets(buckets);
      setTimeMetrics(metrics);

      let defaultMetric = selectedMetric;
      if (!defaultMetric || !metrics.includes(defaultMetric)) {
        defaultMetric = metrics.includes('Sign Created')
          ? 'Sign Created'
          : metrics.includes('Downloaded')
          ? 'Downloaded'
          : metrics[0];
      }

      setSelectedMetric(defaultMetric);
      setBarData(computeBarDataFromBuckets(buckets, defaultMetric));
      setAnalyticsStatus('OK');
    } catch (err) {
      console.error('Failed to run analytics or load JSON:', err);
      setAnalyticsStatus('Error running analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleUserChange = (e) => {
    const username = e.target.value;
    setSelectedUser(username);

    if (!username || !userDataMap || typeof userDataMap !== 'object') {
      setBarData([]);
      return;
    }

    const userStats = userDataMap[username];
    if (!userStats || typeof userStats !== 'object') {
      setBarData([]);
      setAnalyticsStatus('No data for selected user');
      return;
    }

    const bars = normalizeJsonToBars(userStats);
    if (!bars.length) {
      setBarData([]);
      setAnalyticsStatus('No data for selected user');
      return;
    }

    const sorted = [...bars].sort((a, b) => b.value - a.value);
    setBarData(sorted.slice(0, 10));
    setAnalyticsStatus(`Showing data for ${username}${selectedKey ? ` (${selectedKey})` : ''}`);
  };

  const handleTimeMetricChange = (e) => {
    const metric = e.target.value;
    setSelectedMetric(metric);
    setBarData(computeBarDataFromBuckets(timeBuckets, metric));
  };

  const maxValue = barData.length > 0 ? Math.max(...barData.map((b) => b.value)) : 0;

  const isUserMode = selectedKey && selectedKey.startsWith('user_');
  const isTimeMode = selectedKey && selectedKey.startsWith('time_');

  useEffect(() => {
    if (!barData || barData.length === 0 || maxValue <= 0) {
      setBarScales([]);
      return;
    }

    setBarScales(new Array(barData.length).fill(0));

    const timeout = setTimeout(() => {
      const scales = barData.map((bar) => {
        if (!bar.value || maxValue === 0) return 0;
        const rawRatio = bar.value / maxValue;
        return Math.max(rawRatio, 0.07);
      });
      setBarScales(scales);
    }, 50);

    return () => clearTimeout(timeout);
  }, [barData, maxValue]);

  const barCount = barData.length;
  const isScrollable = barCount > MAX_VISIBLE_BARS;

  useEffect(() => {
    if (!isScrollable || !scrollContainerRef.current) return;
    const id = setTimeout(() => {
      const el = scrollContainerRef.current;
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      if (maxScrollLeft > 0) {
        el.scrollTo({ left: maxScrollLeft, behavior: 'smooth' });
      }
    }, 80);

    return () => clearTimeout(id);
  }, [barCount, isScrollable]);

  const startDragging = (clientX) => {
    if (!scrollContainerRef.current) return;
    isDraggingRef.current = true;
    setIsDragging(true);
    dragStartXRef.current = clientX;
    dragStartScrollLeftRef.current = scrollContainerRef.current.scrollLeft;
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
    setIsDragging(false);
  };

  const handleMouseDown = (e) => {
    if (!isScrollable) return;
    e.preventDefault();
    startDragging(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current || !scrollContainerRef.current) return;
    e.preventDefault();
    const dx = e.clientX - dragStartXRef.current;
    scrollContainerRef.current.scrollLeft = dragStartScrollLeftRef.current - dx;
  };

  const handleMouseUp = () => {
    if (isDraggingRef.current) stopDragging();
  };

  const handleMouseLeave = () => {
    if (isDraggingRef.current) stopDragging();
  };

  const handleTouchStart = (e) => {
    if (!isScrollable) return;
    if (e.touches.length !== 1) return;
    startDragging(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDraggingRef.current || !scrollContainerRef.current) return;
    if (e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - dragStartXRef.current;
    scrollContainerRef.current.scrollLeft = dragStartScrollLeftRef.current - dx;
  };

  const handleTouchEnd = () => {
    if (isDraggingRef.current) stopDragging();
  };

  let xAxisLabel = 'Metrics';
  if (currentTimeframe === '24_hours_hourly') xAxisLabel = 'Hours';
  else if (currentTimeframe === '7_days_daily' || currentTimeframe === '30_days_daily') xAxisLabel = 'Days';
  else if (currentTimeframe === '1_year_weekly') xAxisLabel = 'Weeks';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '10px',
          padding: '20px',
          width: '90%',
          maxWidth: '1200px',
          height: '85%',
          maxHeight: '85%',
          boxShadow: '0 0 18px rgba(0,0,0,0.4)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <h3 style={{ margin: 0 }}>Analytics</h3>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '4px 10px',
              backgroundColor: '#333',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              marginLeft: '10px',
            }}
          >
            Close
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            marginBottom: '16px',
            gap: '8px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
            <span style={{ fontWeight: 'bold' }}>By User:</span>
            <button style={makeButtonStyle('user_24_hours')} onClick={() => runAnalytics('user_24_hours')}>
              24 Hours
            </button>
            <button style={makeButtonStyle('user_7_days')} onClick={() => runAnalytics('user_7_days')}>
              7 Days
            </button>
            <button style={makeButtonStyle('user_30_days')} onClick={() => runAnalytics('user_30_days')}>
              30 Days
            </button>
            <button style={makeButtonStyle('user_1_year')} onClick={() => runAnalytics('user_1_year')}>
              1 Year
            </button>

            {isUserMode && userList.length > 0 && (
              <select
                value={selectedUser}
                onChange={handleUserChange}
                style={{
                  marginLeft: '10px',
                  padding: '3px 6px',
                  fontSize: '12px',
                  borderRadius: '4px',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: '#ccc',
                }}
              >
                <option value="">Select user…</option>
                {userList.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
            <span style={{ fontWeight: 'bold' }}>By Time:</span>
            <button style={makeButtonStyle('time_24_hours')} onClick={() => runAnalytics('time_24_hours')}>
              24 Hours
            </button>
            <button style={makeButtonStyle('time_7_days')} onClick={() => runAnalytics('time_7_days')}>
              7 Days
            </button>
            <button style={makeButtonStyle('time_30_days')} onClick={() => runAnalytics('time_30_days')}>
              30 Days
            </button>
            <button style={makeButtonStyle('time_1_year')} onClick={() => runAnalytics('time_1_year')}>
              1 Year
            </button>

            {isTimeMode && timeMetrics.length > 0 && (
              <select
                value={selectedMetric}
                onChange={handleTimeMetricChange}
                style={{
                  marginLeft: '10px',
                  padding: '3px 6px',
                  fontSize: '12px',
                  borderRadius: '4px',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: '#ccc',
                }}
              >
                {timeMetrics.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div
          style={{
            flex: 1,
            border: '1px solid #ccc',
            borderRadius: '6px',
            backgroundColor: '#fafafa',
            padding: '1vh 1vw',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5vh',
              fontSize: 13,
              color: '#555',
              gap: '8px',
            }}
          >
            <span style={{ fontSize: 12, color: analyticsLoading ? '#007bff' : '#888' }}>
              {analyticsLoading ? 'Running analytics...' : analyticsStatus || 'Select a timeframe to load analytics'}
            </span>
            {lastUpdated && (
              <span style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>
                Last updated: {lastUpdated}
              </span>
            )}
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
            <div
              style={{
                width: '5vw',
                minWidth: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingBottom: '0.5vh',
              }}
            >
              <span
                style={{
                  transform: 'rotate(-90deg)',
                  transformOrigin: 'center',
                  fontSize: 11,
                  color: '#555',
                  whiteSpace: 'nowrap',
                }}
              >
                Count
              </span>
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
{/* Bars area */}
<div
  ref={scrollContainerRef}
  style={{
    flex: 1,
    display: 'flex',
    alignItems: 'stretch', // ✅ keep stretching so height: '100%' works
    justifyContent: 'flex-start',
    overflowX: isScrollable ? 'auto' : 'hidden',
    overflowY: 'hidden',
    padding: '0 1vw',
    cursor: isScrollable ? (isDragging ? 'grabbing' : 'grab') : 'default',
    userSelect: isDragging ? 'none' : 'auto',
    WebkitUserSelect: isDragging ? 'none' : 'auto',
  }}
  onMouseDown={handleMouseDown}
  onMouseMove={handleMouseMove}
  onMouseUp={handleMouseUp}
  onMouseLeave={handleMouseLeave}
  onTouchStart={handleTouchStart}
  onTouchMove={handleTouchMove}
  onTouchEnd={handleTouchEnd}
>
  {barData.length === 0 ? (
    <div
      style={{
        fontSize: 12,
        color: '#999',
        textAlign: 'center',
        width: '100%',
        alignSelf: 'center',
      }}
    >
      No data to display yet.
    </div>
  ) : (
    barData.map((bar, i) => {
      const baseColor = BAR_COLORS[i % BAR_COLORS.length];
      const scale = barScales[i] ?? 0;

      const multi = labelNeedsMoreRoom(bar.label);
      const labelText = makeTiltLabelText(bar.label);

return (
  <div
    key={i}
    style={{
      height: '100%', // ✅ each bar column fills the available graph height
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-end',
      fontSize: 11,
      margin: '0 0.5vw',
      flex: `0 0 ${BAR_WIDTH_PERCENT}%`,
      maxWidth: `${BAR_WIDTH_PERCENT}%`,
      userSelect: 'none',
      WebkitUserSelect: 'none',
    }}
  >
    {/* Track: identical height across all bars (because label height is fixed) */}
    <div
      style={{
        width: '100%',
        flex: 1, // ✅ same bar baseline for all columns
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: '0 0.2vw',
        minHeight: 0,
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          borderRadius: '6px',
          backgroundColor: 'rgba(0,0,0,0.03)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            backgroundColor: baseColor,
            borderRadius: '6px',
            boxShadow: '0 3px 6px rgba(0,0,0,0.2)',
            transformOrigin: 'bottom center',
            transform: `scaleY(${scale})`,
            transition: 'transform 0.4s ease-out',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: '4px',
            transform: 'translateX(-50%) rotate(-90deg)',
            transformOrigin: 'center',
            width: 'auto',
            textAlign: 'center',
            fontFamily: 'monospace',
            fontWeight: 'bold',
            fontSize: 9,
            color: '#ffffff',
            textShadow: '0 0 3px rgba(0,0,0,0.7)',
            whiteSpace: 'nowrap',
          }}
        >
          {bar.value}
        </div>
      </div>
    </div>

    {/* ✅ Label area: constant height for ALL bars + no mid-word breaking */}
    <div
      style={{
        marginTop: '0.4vh',
        height: '14vh',     // ✅ constant label area height -> bars align at bottom
        maxHeight: '14vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flex: '0 0 auto',
      }}
    >
      <span
        style={{
          transform: 'rotate(-90deg)',
          transformOrigin: 'center',
          whiteSpace: 'pre',        // ✅ respects '\n' exactly, no auto wrapping
          wordBreak: 'normal',      // ✅ prevent "deduct" + "ed"
          overflowWrap: 'normal',   // ✅ prevent forced breaks on narrow widths
          lineHeight: 1.15,
          textAlign: 'center',
          display: 'inline-block',
        }}
      >
        {makeTiltLabelText(bar.label)}
      </span>
    </div>
  </div>
);

    })
  )}
</div>


              <div style={{ marginTop: '0.8vh', textAlign: 'center', fontSize: 11, color: '#555' }}>
                {xAxisLabel}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
